// src/pages/Mailbox.jsx
import React, { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

export default function Mailbox() {
  const seo = seoPresets["/settings/mail"];
  const [loading, setLoading] = useState(true);
  const [mailboxes, setMailboxes] = useState([]);
  const [showBanner, setShowBanner] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);

  const hasMailbox = mailboxes.some((m) => m.status === "connected");

  async function refresh() {
    setLoading(true);
    try {
      const j = await apiFetch("/api/mailboxes", { credentials: "include" });
      const items = Array.isArray(j.items) ? j.items : [];
      setMailboxes(items);
      setShowBanner(!items.some((m) => m.status === "connected"));
    } catch {
      setMailboxes([]);
      setShowBanner(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function startGoogle() {
    const j = await apiFetch("/api/mailboxes/google/start", {
      method: "POST",
      credentials: "include",
    });
    if (j?.url) window.location.href = j.url;
  }

  async function startOutlook() {
    const j = await apiFetch("/api/mailboxes/outlook/start", {
      method: "POST",
      credentials: "include",
    });
    if (j?.url) window.location.href = j.url;
  }

  const ProviderBtn = ({ onClick, icon, label, disabled }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className="w-full md:w-64 flex items-center justify-center gap-3 rounded-2xl border border-white/10 bg-white/5 hover:bg-white/10 disabled:opacity-50 py-4 px-5 transition shadow-sm"
    >
      <span className="text-xl">{icon}</span>
      <span className="font-medium">{label}</span>
    </button>
  );

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="min-h-[70vh] w-full bg-neutral-950 text-neutral-100">
        {showBanner && (
          <div className="bg-amber-400/10 border border-amber-400/30 text-amber-200 mx-auto max-w-5xl mt-6 rounded-xl p-4">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="font-semibold">You have no mailboxes linked</div>
                <div className="text-sm opacity-80">
                  Connect your email to send campaigns via Musicbase.
                </div>
              </div>
              <button
                onClick={() =>
                  window.scrollTo({
                    top: document.body.scrollHeight,
                    behavior: "smooth",
                  })
                }
                className="rounded-lg bg-amber-400 text-black font-semibold px-4 py-2"
              >
                Link mailbox
              </button>
            </div>
          </div>
        )}

        <section className="mx-auto max-w-6xl px-6 md:px-10 pt-16 pb-10">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Connect your email to get started
          </h1>
          <p className="mt-3 text-neutral-300 max-w-2xl">
            Use your own inbox for outreach, replies, and deliverability. Choose
            Google or Outlook. You can skip now and connect later.
          </p>
        </section>

        <section className="mx-auto max-w-5xl px-6 md:px-10 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <ProviderBtn
              onClick={startGoogle}
              label="Google (Gmail / Workspace)"
              icon={<span>🟢</span>}
            />
            <ProviderBtn
              onClick={startOutlook}
              label="Outlook (coming soon)"
              icon={<span>🔵</span>}
              disabled
            />
            <ProviderBtn
              onClick={() => { }}
              label="Other (IMAP/SMTP) — soon"
              icon={<span>✉️</span>}
              disabled
            />
          </div>

          <label className="flex items-start gap-3 mt-8 text-sm text-neutral-300">
            <input
              type="checkbox"
              className="mt-1"
              checked={acceptTerms}
              onChange={(e) => setAcceptTerms(e.target.checked)}
            />
            <span>
              I will comply with Musicbase Terms when sending emails and won’t
              send spam or harassing content. I acknowledge inbox data may be used
              to provide deliverability features.
            </span>
          </label>

          <div className="mt-6">
            <button
              className="rounded-2xl bg-white text-black font-semibold px-6 py-3 disabled:opacity-50"
              disabled={!acceptTerms}
            >
              Connect email
            </button>
            <button
              className="ml-3 text-neutral-300 hover:text-white"
              onClick={() => window.history.back()}
            >
              Skip for now
            </button>

            {!acceptTerms && (
              <p className="mt-2 text-xs text-neutral-400">
                Please accept the terms above to enable the{" "}
                <span className="font-semibold">Connect email</span> button.
              </p>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-6 md:px-10 pb-16 border-t border-white/10">
          <div className="flex items-center justify-between mt-10 mb-4">
            <h2 className="text-xl font-semibold">Linked mailboxes</h2>
            <button
              className="text-sm text-neutral-300 hover:text-white"
              onClick={refresh}
            >
              Refresh
            </button>
          </div>
          {loading ? (
            <div className="text-neutral-400">Loading…</div>
          ) : !mailboxes.length ? (
            <div className="text-neutral-400">None yet.</div>
          ) : (
            <ul className="space-y-3">
              {mailboxes.map((m, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-white/10 p-4 flex items-center justify-between"
                >
                  <div>
                    <div className="font-medium">{m.email || m.address}</div>
                    <div className="text-sm text-neutral-400">
                      {m.provider} • {m.status}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="rounded-lg bg-white/10 hover:bg-white/15 px-3 py-2 text-sm">
                      Set default
                    </button>
                    <button className="rounded-lg bg-white/10 hover:bg-white/15 px-3 py-2 text-sm">
                      Send test
                    </button>
                    <button className="rounded-lg bg-white/10 hover:bg-white/15 px-3 py-2 text-sm">
                      Disconnect
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </>
  );
}